import { useState, useEffect, useCallback } from 'react';
import { BodyParagraphData, BodyParagraphType } from '@/types/writing';

interface UseEssayCoreStateProps {
  initialPrompt: string;
  initialIntroduction: string;
  initialBodyParagraphs: BodyParagraphData[];
  initialConclusion: string;
  initialEssayId?: string | null;
}

export function useEssayCoreState({
  initialPrompt,
  initialIntroduction,
  initialBodyParagraphs,
  initialConclusion,
  initialEssayId,
}: UseEssayCoreStateProps) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [introduction, setIntroduction] = useState(initialIntroduction);
  const [bodyParagraphs, setBodyParagraphs] = useState<BodyParagraphData[]>(
    initialBodyParagraphs
  );
  const [conclusion, setConclusion] = useState(initialConclusion);

  useEffect(() => {
    const validInitialParas: BodyParagraphData[] =
      initialBodyParagraphs && initialBodyParagraphs.length > 0
        ? initialBodyParagraphs
        : [
            { type: '', content: '' },
            { type: '', content: '' },
          ];
    setPrompt(initialPrompt);
    setIntroduction(initialIntroduction);
    setBodyParagraphs(validInitialParas);
    setConclusion(initialConclusion);
  }, [
    initialEssayId,
    initialPrompt,
    initialIntroduction,
    initialBodyParagraphs,
    initialConclusion,
  ]);

  const handleIntroductionChange = useCallback(
    (value: string) => setIntroduction(value),
    []
  );
  const handleConclusionChange = useCallback(
    (value: string) => setConclusion(value),
    []
  );
  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => setPrompt(e.target.value),
    []
  );

  const handleBodyParagraphChange = useCallback(
    (idx: number, value: string) => {
      setBodyParagraphs((paragraphs) =>
        paragraphs.map((p, i) => (i === idx ? { ...p, content: value } : p))
      );
    },
    []
  );

  const handleBodyTypeChange = useCallback(
    (idx: number, type: BodyParagraphType) => {
      setBodyParagraphs((paragraphs) =>
        paragraphs.map((p, i) => (i === idx ? { ...p, type: type } : p))
      );
    },
    []
  );

  const addBodyParagraph = useCallback(() => {
    setBodyParagraphs((paragraphs) => [
      ...paragraphs,
      { type: '', content: '' },
    ]);
  }, []);

  const removeBodyParagraph = useCallback((idx: number) => {
    setBodyParagraphs((paragraphs) => paragraphs.filter((_, i) => i !== idx));
  }, []);

  return {
    prompt,
    setPrompt,
    introduction,
    bodyParagraphs,
    conclusion,
    handleIntroductionChange,
    handleConclusionChange,
    handlePromptChange,
    handleBodyParagraphChange,
    handleBodyTypeChange,
    addBodyParagraph,
    removeBodyParagraph,
  };
}
